import "./App.scss";
import { ComponentType } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router";
import {
  DesignerApplicationPage,
  LandingPage,
  Registration,
  SmartGuildLandingPage,
} from "./pages";
import { LoginPage } from "./pages/login";
import { DashboardPage } from "./pages/dashboard-page";
import { DesignerTable } from "./pages/admin-dashboard-page/designer-table";
import { HomeownerAccountsPage } from "./pages/admin-dashboard-page/homeowner-accounts-page";
import { MatchOversight } from "./pages/admin-dashboard-page/match-oversight";
import AdminMessagesPage from "./pages/admin-dashboard-page/messages";
import AdminMembershipPlansPage from "./pages/admin-dashboard-page/membership-plans";
import AdminFeatureFlagsPage from "./pages/admin-dashboard-page/feature-flags";
import AdminUserManagement from "./pages/admin-dashboard-page/admin-user-management";
import AdminDmaAvailabilityPage from "./pages/admin-dashboard-page/dma-availability";
import AdminEventAttributionPage from "./pages/admin-dashboard-page/event-attribution";
import { DesignerApplicationContextProvider } from "./hooks/designer-application-context";
import { createTheme, ThemeProvider } from "@mui/material";
import { JoinAsADesignerPage } from "./pages/join-as-a-designer-page";
import { ForgotPasswordPage } from "./pages/forgot-password";
import { Pages } from "./utils";
import { MyAccountPage } from "./pages/my-account-page";
import { DashboardLayout } from "./components/static-page-components/dashboard-layout";
import { LayoutRouter } from "./components/layout-router";
import { DesignCommunityPage } from "./pages/design-community-page";
import { AboutDesignHubPage } from "./pages/about-design-hub-page";
import { FinancingPage } from "./pages/financing-page";
import { ComingSoonPage } from "./pages/coming-soon-page";
import ContactUsPage from "./pages/contact-us-page";
import { MembershipPricingPage } from "./pages/membership-pricing-page";
import { AboutUsPage } from "./pages/about-us-page";
import { EventsLandingPage } from "./pages/event-cards-landing-page";
import { LaunchPage } from "./pages/launch-page";
import { TerritoriesPage } from "./pages/territories-page";
import { HomeownerPage } from "./pages/homeowner-page";
import { customTheme } from "./utils/cutom-theme";
import { TermsOfUsePage } from "./pages/terms-of-use-page";
import { PolicyPage } from "./pages/policy-page";
import { CopyrightTrademarkPage } from "./pages/copyright-trademark-page";
import { AddDesignerFilePage } from "./pages/add-designer-file-page";
import { MyDesignFilesPage } from "./pages/my-design-files-page";
import { AddDesignBoardView } from "./pages/add-designer-file-page/add-design-board";
import { AddBeforeAndAfterView } from "./pages/add-designer-file-page/add-before-and-after";
import { AddProjectView } from "./pages/add-designer-file-page/add-project";
import { SelectDesignerFileTypeView } from "./pages/add-designer-file-page/select-designer-file-type";
import { MyMatchingProfilePage } from "./pages/my-matching-profile";
import { DesignerFilesProjectsPage } from "./pages/designer-files-projects-page";
import { DesignerFilesPublicPage } from "./pages/designer-files-public-page";
import { MyIdeaBooksPage } from "./pages/my-idea-books-page";
import { MyIdeaBookDetailPage } from "./pages/my-idea-book-detail-page";
import { HomeownerUserLandingPage } from "./pages";
import { MyBioRoute } from "./pages/my-bio-page/my-bio-route";
import HomeownerProjectsPage from "./pages/homeowner-projects-page";
import HomeownerProjectCreatePage from "./pages/homeowner-projects-page/create-project-page";
import { HomeownerProfilePage } from "./pages/homeowner-profile";
import { DesignerPremiumProfilePage } from "./pages/designer-profile";
import { DesignerCompatibilityDetailsPage } from "./pages/designer-compatibility-details-page";
import { DesignerProjectDetailsPage } from "./pages/designer-project-details";
import { NotificationProvider } from "./contexts/NotificationContext";
import { PlanProvider } from "./contexts/PlanContext";
import { useUserContext } from "./hooks/userContext";
import { isAdminUser } from "./utils";
import { YourHubView } from "./pages/your-hub-page";
import { HomeownerMembershipPricingPage } from "./pages/homeowner-membership-pricing-page";
import { RootLayout } from "./components/RootLayout";
import { PaymentProcessingPage } from "./pages/payment-processing";
import { PaymentSuccessRedirectPage } from "./pages/payment-success-redirect";
import OrderSummary from "./pages/OrderSummary";
import PaymentConfirmation from "./pages/payment-confirmation";
import PaymentPage from "./pages/payment-page";
import { RegistrationGuard } from "./components/registration-guard";
import { DesignerMembershipSelectionPage } from "./pages/designer-membership-selection";
import {
  AuthenticatedRoute,
  DesignerOnlyRoute,
  HomeownerOnlyRoute,
  DashboardAccessGuard,
  TierGuard,
} from "./components/guards";
import { DesignerMeetingSuccessPage } from "./pages/designer-meeting-success";
import { AdminDashboardPage } from "./pages/admin-dashboard-page";
import { MatchDetailPage, MatchesPage } from "./pages/matches-page";
import MessagesPage from "./pages/messages-page";
import PersonalityQuizPage from "./pages/personality-quiz-page";
import HomeownerPersonalityQuizPage from "./pages/homeowner-personality-quiz-page";
import { GuildGuidesPage } from "./pages/guild-guides-page";

const theme = createTheme(customTheme);
const AdminOnlyRoute = ({ component: Component }: { component: ComponentType }) => {
  const { user } = useUserContext();
  if (!isAdminUser(user)) {
    return <Navigate to={Pages.DASHBOARD} replace />;
  }
  return (
    <DesignerApplicationContextProvider>
      <Component />
    </DesignerApplicationContextProvider>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        path: Pages.SIGNUP,
        element: <Registration />,
      },
      {
        path: Pages.LOGIN,
        element: <LoginPage />,
      },
      {
        path: Pages.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: Pages.DESIGNER_MEETING_SUCCESS,
        element: (
          <DesignerApplicationContextProvider>
            <DesignerMeetingSuccessPage />
          </DesignerApplicationContextProvider>
        ),
      },
      {
        path: "/payment-processing/:token",
        element: <PaymentProcessingPage />,
      },
      {
        path: "/payment-success",
        element: <PaymentSuccessRedirectPage />,
      },
      {
        path: "/order-summary/:token",
        element: <OrderSummary />,
      },
      {
        path: "/order-summary/default",
        element: <OrderSummary />,
      },
      {
        path: "/payment-confirmation",
        element: <PaymentConfirmation />,
      },
      {
        path: "/payment-page",
        element: (
          <PlanProvider>
            <PaymentPage
              amount={29.99}
              planName="Guild"
              planType="Guild"
              billingCycle="Monthly"
              email="test@example.com"
              homeownerId="test-homeowner-id"
              customerInfo={{
                firstName: "John",
                lastName: "Doe",
                address: "123 Main Street",
                city: "Anytown",
                state: "CA",
                zipCode: "12345",
                phoneNumber: "(555) 123-4567",
              }}
              plan={{
                _id: "test-plan-id",
                slug: "guild",
                name: "Guild",
                description: "Full access to our design community",
                tagLine: "Join the Guild",
                benefits: [
                  "Curated Project Gallery",
                  "Guild Guide Articles", 
                  "Powerful Filters",
                  "Create & Save to Idea Books"
                ],
                pricing: {
                  price: 29.99,
                  originalPrice: null,
                  currency: "USD",
                  period: "monthly"
                },
                public: true,
                perks: [],
                primaryPlan: true,
                highlevelProductId: "test-product-id",
                highlevelPriceId: "test-price-id",
                metadata: {
                  features: [
                    "Curated Project Gallery",
                    "Guild Guide Articles", 
                    "Powerful Filters",
                    "Create & Save to Idea Books"
                  ]
                }
              }}
              onPaymentSuccess={(response) => {
                console.log('Payment successful:', response);
                alert(`Payment successful! Transaction ID: ${response.transactionId}`);
              }}
              onPaymentFailure={(errorMessage) => {
                console.error('Payment failed:', errorMessage);
                alert(`Payment failed: ${errorMessage}`);
              }}
            />
          </PlanProvider>
        ),
      },
      {
        path: "/payment-page-guild-plus",
        element: (
          <PlanProvider>
            <PaymentPage
              amount={149.99}
              planName="Guild Plus"
              planType="GuildPlus"
              billingCycle="Annually"
              email="test@example.com"
              homeownerId="test-homeowner-id"
              customerInfo={{
                firstName: "Jane",
                lastName: "Smith",
                address: "456 Oak Avenue",
                city: "Springfield",
                state: "IL",
                zipCode: "62701",
                phoneNumber: "(555) 987-6543",
              }}
              plan={{
                _id: "test-plan-plus-id",
                slug: "guild-plus",
                name: "Guild Plus",
                description: "Premium access with exclusive benefits",
                tagLine: "Join the Guild Plus",
                benefits: [
                  "Curated Project Gallery",
                  "Guild Guide Articles", 
                  "Powerful Filters",
                  "Create & Save to Idea Books",
                  "Priority Designer Matching",
                  "Exclusive Design Consultations",
                  "Advanced Project Analytics"
                ],
                pricing: {
                  price: 149.99,
                  originalPrice: null,
                  currency: "USD",
                  period: "annually"
                },
                public: true,
                perks: [],
                primaryPlan: false,
                highlevelProductId: "test-product-plus-id",
                highlevelPriceId: "test-price-plus-id",
                metadata: {
                  features: [
                    "Curated Project Gallery",
                    "Guild Guide Articles", 
                    "Powerful Filters",
                    "Create & Save to Idea Books",
                    "Priority Designer Matching",
                    "Exclusive Design Consultations",
                    "Advanced Project Analytics"
                  ]
                }
              }}
              onPaymentSuccess={(response) => {
                console.log('Payment successful:', response);
                alert(`Payment successful! Transaction ID: ${response.transactionId}`);
              }}
              onPaymentFailure={(errorMessage) => {
                console.error('Payment failed:', errorMessage);
                alert(`Payment failed: ${errorMessage}`);
              }}
            />
          </PlanProvider>
        ),
      },
      {
        path: "/",
        element: <LayoutRouter />,
        children: [
          {
            path: Pages.HOME,
            element: <LandingPage />,
          },
          {
            path: Pages.SMART_GUILD_MATCHING,
            element: <SmartGuildLandingPage />,
          },
          {
            path: Pages.JOIN_AS_DESIGNER,
            element: <JoinAsADesignerPage />,
          },
          {
            path: Pages.DESIGN_COMMUNITY,
            element: <DesignCommunityPage />,
          },
          {
            path: Pages.ABOUT_DESIGN_HUB,
            element: <AboutDesignHubPage />,
          },
          {
            path: Pages.FINANCING,
            element: <FinancingPage />,
          },
          {
            path: Pages.COMING_SOON,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.CONTACT_US,
            element: <ContactUsPage />,
          },
          {
            path: Pages.DESIGNER_MEMBERSHIP_PRICING,
            element: <MembershipPricingPage />,
          },
          {
            path: Pages.HOMEOWNER_MEMBERSHIP_PRICING,
            element: <HomeownerMembershipPricingPage />,
          },
          {
            path: Pages.ABOUT_US_PAGE,
            element: <AboutUsPage />,
          },
          {
            path: Pages.EVENTS,
            element: <EventsLandingPage />,
          },
          {
            path: Pages.LAUNCH,
            element: <LaunchPage />,
          },
          {
            path: Pages.TERMS_OF_SERVICE,
            element: <TermsOfUsePage />,
          },
          {
            path: Pages.PRIVACY_POLICY,
            element: <PolicyPage />,
          },
          {
            path: Pages.COPYRIGHT_TRADEMARK,
            element: <CopyrightTrademarkPage />,
          },
          {
            path: Pages.DESIGNER_APPLICATION,
            element: (
              <DesignerApplicationContextProvider>
                <DesignerApplicationPage />
              </DesignerApplicationContextProvider>
            ),
          },
          {
            path: Pages.TERRITORIES,
            element: <TerritoriesPage />,
          },
          {
            path: Pages.HOMEOWNER_LANDING,
            element: <HomeownerPage />,
          },
          {
            path: Pages.HOMEOWNER_USER_LANDING,
            element: <HomeownerUserLandingPage />,
          },
          {
            path: Pages.HOMEOWNER_GUILD_GUIDES,
            element: <GuildGuidesPage access="public" />,
          },
          {
            path: Pages.HOMEOWNER_GUILD_GUIDE_POST,
            element: <GuildGuidesPage access="public" />,
          },
          {
            path: Pages.DESIGNER_FILES_PUBLIC,
            element: <DesignerFilesPublicPage />,
          },
          {
            path: Pages.PERSONALITY_QUIZ,
            element: <Navigate to={Pages.DESIGNER_PERSONALITY_QUIZ} replace />,
          },
          {
            path: Pages.DESIGNER_PERSONALITY_QUIZ,
            element: <PersonalityQuizPage />,
          },
          {
            path: Pages.HOMEOWNER_PERSONALITY_QUIZ,
            element: <HomeownerPersonalityQuizPage />,
          },
        ],
      },
      {
        path: "/",
        element: (
          <RegistrationGuard>
            <DashboardAccessGuard>
              <DashboardLayout />
            </DashboardAccessGuard>
          </RegistrationGuard>
        ),
        children: [
          {
            path: Pages.DASHBOARD,
            element: (
              <DesignerApplicationContextProvider>
                <DashboardPage />
              </DesignerApplicationContextProvider>
            ),
          },
          {
            path: Pages.ADMIN_DASHBOARD,
            element: <AdminOnlyRoute component={AdminDashboardPage} />,
          },
          {
            path: Pages.ADMIN_DESIGNER_APPLICATIONS,
            element: <AdminOnlyRoute component={DesignerTable} />,
          },
          {
            path: Pages.ADMIN_HOMEOWNER_ACCOUNTS,
            element: <AdminOnlyRoute component={HomeownerAccountsPage} />,
          },
          {
            path: Pages.ADMIN_MATCHES,
            element: <AdminOnlyRoute component={MatchOversight} />,
          },
          {
            path: Pages.ADMIN_MEMBERSHIP_PLANS,
            element: <AdminOnlyRoute component={AdminMembershipPlansPage} />,
          },
          {
            path: Pages.ADMIN_FEATURE_FLAGS,
            element: <AdminOnlyRoute component={AdminFeatureFlagsPage} />,
          },
          {
            path: Pages.ADMIN_DMA_AVAILABILITY,
            element: <AdminOnlyRoute component={AdminDmaAvailabilityPage} />,
          },
          {
            path: Pages.ADMIN_MESSAGES,
            element: <AdminOnlyRoute component={AdminMessagesPage} />,
          },
          {
            path: Pages.ADMIN_EVENT_ATTRIBUTION,
            element: <AdminOnlyRoute component={AdminEventAttributionPage} />,
          },
          {
            path: Pages.ADMIN_USER_MANAGEMENT,
            element: <AdminOnlyRoute component={AdminUserManagement} />,
          },
          {
            path: Pages.YOUR_HUB,
            element: <YourHubView />,
          },
          {
            path: Pages.MY_ACCOUNT,
            element: <MyAccountPage />,
          },
          {
            path: Pages.PROFILE,
            element: <Navigate to={Pages.MY_ACCOUNT} replace />,
          },
      {
        path: Pages.MY_BIO,
        element: <MyBioRoute />,
      },
      {
        path: Pages.MY_PROJECTS,
        element: (
          <HomeownerOnlyRoute>
            <HomeownerProjectsPage />
          </HomeownerOnlyRoute>
        ),
      },
      {
        path: Pages.MY_PROJECTS_CREATE,
        element: (
          <HomeownerOnlyRoute>
            <HomeownerProjectCreatePage />
          </HomeownerOnlyRoute>
        ),
      },
      {
        path: Pages.MY_PROJECTS_EDIT,
        element: (
          <HomeownerOnlyRoute>
            <HomeownerProjectCreatePage />
          </HomeownerOnlyRoute>
        ),
      },
      {
        path: Pages.HOMEOWNER_PROFILE_SELF,
        element: (
          <HomeownerOnlyRoute>
            <TierGuard
              requiresTier="Guild"
              redirectTo={Pages.HOMEOWNER_MEMBERSHIP_PRICING}
            >
              <HomeownerProfilePage />
            </TierGuard>
          </HomeownerOnlyRoute>
        ),
      },
      {
        path: Pages.HOMEOWNER_PROFILE,
        element: <HomeownerProfilePage />,
      },
      {
        path: Pages.DESIGNER_PREMIUM_PROFILE,
        element: (
          <AuthenticatedRoute>
            <DesignerPremiumProfilePage />
          </AuthenticatedRoute>
        ),
      },
      {
        path: Pages.DESIGNER_COMPATIBILITY_DETAILS,
        element: (
          <AuthenticatedRoute>
            <DesignerCompatibilityDetailsPage />
          </AuthenticatedRoute>
        ),
      },
      {
        path: Pages.DESIGNER_PROJECT_DETAILS,
        element: (
          <AuthenticatedRoute>
            <DesignerProjectDetailsPage />
          </AuthenticatedRoute>
        ),
      },
          {
            path: Pages.DESIGNER_MEMBERSHIP_SELECTION,
            element: (
              <DesignerApplicationContextProvider>
                <DesignerMembershipSelectionPage />
              </DesignerApplicationContextProvider>
            ),
          },
          {
            path: Pages.MY_MATCHING_PROFILE,
            element: (
                  <MyMatchingProfilePage />
            ),
          },
          {
            path: Pages.MY_DESIGN_FILES,
            element: (
              <DesignerOnlyRoute>
                <MyDesignFilesPage />
              </DesignerOnlyRoute>
            ),
          },
          {
            path: Pages.DESIGNER_FILES_PROJECTS,
            element: (
              <DesignerOnlyRoute>
                <DesignerFilesProjectsPage />
              </DesignerOnlyRoute>
            ),
          },
          {
            path: Pages.DESIGNER_FILES_HOMEOWNER,
            element: (
              <HomeownerOnlyRoute>
                <TierGuard
                  requiresTier="Guild"
                  redirectTo={Pages.DESIGNER_FILES_PUBLIC}
                  allowAdminOverride
                >
                  <DesignerFilesPublicPage />
                </TierGuard>
              </HomeownerOnlyRoute>
            ),
          },
          {
            path: Pages.MY_IDEA_BOOKS,
            element: (
              <HomeownerOnlyRoute>
                <TierGuard requiresTier="Guild" redirectTo={Pages.DASHBOARD}>
                  <MyIdeaBooksPage />
                </TierGuard>
              </HomeownerOnlyRoute>
            ),
          },
          {
            path: Pages.MY_IDEA_BOOK_DETAILS,
            element: (
              <HomeownerOnlyRoute>
                <TierGuard requiresTier="Guild" redirectTo={Pages.DASHBOARD}>
                  <MyIdeaBookDetailPage />
                </TierGuard>
              </HomeownerOnlyRoute>
            ),
          },
          {
            path: Pages.MESSAGES,
            element: <MessagesPage />,
          },
          {
            path: Pages.MATCHES,
            element: <MatchesPage />,
          },
          {
            path: Pages.GUILD_GUIDES,
            element: (
              <HomeownerOnlyRoute>
                <TierGuard
                  requiresTier="Guild"
                  redirectTo={Pages.HOMEOWNER_GUILD_GUIDES}
                >
                  <GuildGuidesPage access="paid" inDashboard />
                </TierGuard>
              </HomeownerOnlyRoute>
            ),
          },
          {
            path: Pages.GUILD_GUIDE_POST,
            element: (
              <HomeownerOnlyRoute>
                <TierGuard
                  requiresTier="Guild"
                  redirectTo={Pages.HOMEOWNER_GUILD_GUIDES}
                >
                  <GuildGuidesPage access="paid" inDashboard />
                </TierGuard>
              </HomeownerOnlyRoute>
            ),
          },
          {
            path: `${Pages.MATCHES}/:matchId`,
            element: <MatchDetailPage />,
          },
          {
            path: Pages.PARTNERS,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.REFER,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.CAMPAIGNS,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.ASSETS,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.DESIGNER_CONFESSIONS,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.CLIENT_FINANCING,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.PROJECT_REPORTING,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.QUICK_LINKS,
            element: <ComingSoonPage />,
          },
          {
            path: Pages.ADD_DESIGNER_FILE,
            element: (
              <DesignerOnlyRoute>
                <AddDesignerFilePage />
              </DesignerOnlyRoute>
            ),
            children: [
              {
                index: true,
                element: <SelectDesignerFileTypeView />,
              },
              {
                path: Pages.ADD_DESIGNER_FILE__BOARD.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", ""),
                element: <AddDesignBoardView />,
              },
              {
                path: `${Pages.ADD_DESIGNER_FILE__BOARD.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", "")}/:id`,
                element: <AddDesignBoardView />,
              },
              {
                path: Pages.ADD_DESIGNER_FILE__BEFORE_AFTER.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", ""),
                element: <AddBeforeAndAfterView />,
              },
              {
                path: `${Pages.ADD_DESIGNER_FILE__BEFORE_AFTER.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", "")}/:id`,
                element: <AddBeforeAndAfterView />,
              },
              {
                path: Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", ""),
                element: <AddProjectView />,
              },
              {
                path: `${Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE.replace(
                  Pages.ADD_DESIGNER_FILE,
                  ""
                ).replace("/", "")}/:id`,
                element: <AddProjectView />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

function App() {
  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <NotificationProvider>
          <PlanProvider>
            <RouterProvider router={router} />
          </PlanProvider>
        </NotificationProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
